import React, { useEffect, useState } from 'react';
import { Globe, MessageSquare } from 'lucide-react';
import toast from 'react-hot-toast';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Spinner } from '../ui/Spinner';
import { submitWebsiteQuoteRequest } from '../../services/leadsApi';

interface WebsiteQuoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  whatsappUrl: string;
}

const initialForm = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  projectType: 'vitrine' as const,
  budget: 'unknown' as const,
  message: '',
  consent: false,
  website: '',
};

export const WebsiteQuoteModal: React.FC<WebsiteQuoteModalProps> = ({
  isOpen,
  onClose,
  whatsappUrl,
}) => {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setForm(initialForm);
      setLoading(false);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitWebsiteQuoteRequest({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        projectType: form.projectType,
        budget: form.budget === 'unknown' ? undefined : form.budget,
        message: form.message.trim(),
        consent: form.consent,
        website: form.website,
      });
      toast.success('Demande envoyée — nous vous répondons sous 48 h.');
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Envoi impossible');
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    'w-full rounded-md border border-gray-600 bg-[#36393F] px-3 py-2 text-sm text-white placeholder-gray-500 focus:border-[#5865F2] focus:outline-none focus:ring-1 focus:ring-[#5865F2]';

  const labelClass = 'mb-1 block text-sm font-medium text-gray-300';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="lg"
      title={
        <span className="flex items-center gap-2 text-white">
          <Globe className="h-6 w-6 shrink-0" aria-hidden />
          Devis création de site web
        </span>
      }
    >
      <p className="mb-4 text-sm text-gray-400">
        Décrivez votre projet : nous vous répondons avec une proposition claire sous{' '}
        <span className="text-gray-200">48 h ouvrées</span>.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="wq-firstName" className={labelClass}>
              Prénom *
            </label>
            <input
              id="wq-firstName"
              name="firstName"
              type="text"
              required
              autoComplete="given-name"
              value={form.firstName}
              onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="wq-lastName" className={labelClass}>
              Nom *
            </label>
            <input
              id="wq-lastName"
              name="lastName"
              type="text"
              required
              autoComplete="family-name"
              value={form.lastName}
              onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
              className={inputClass}
            />
          </div>
        </div>

        <div>
          <label htmlFor="wq-email" className={labelClass}>
            Email *
          </label>
          <input
            id="wq-email"
            name="email"
            type="email"
            required
            autoComplete="email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor="wq-phone" className={labelClass}>
            Téléphone <span className="text-gray-500">(optionnel)</span>
          </label>
          <input
            id="wq-phone"
            name="phone"
            type="tel"
            autoComplete="tel"
            value={form.phone}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            className={inputClass}
          />
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="wq-projectType" className={labelClass}>
              Type de projet *
            </label>
            <select
              id="wq-projectType"
              name="projectType"
              required
              value={form.projectType}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  projectType: e.target.value as typeof form.projectType,
                }))
              }
              className={inputClass}
            >
              <option value="vitrine">Site vitrine</option>
              <option value="ecommerce">E-commerce</option>
              <option value="landing">Landing page</option>
              <option value="refonte">Refonte de site</option>
              <option value="autre">Autre</option>
            </select>
          </div>
          <div>
            <label htmlFor="wq-budget" className={labelClass}>
              Budget indicatif
            </label>
            <select
              id="wq-budget"
              name="budget"
              value={form.budget}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  budget: e.target.value as typeof form.budget,
                }))
              }
              className={inputClass}
            >
              <option value="unknown">Je ne sais pas encore</option>
              <option value="under2k">Moins de 2 000 €</option>
              <option value="2k5k">2 000 € – 5 000 €</option>
              <option value="5k10k">5 000 € – 10 000 €</option>
              <option value="10kplus">Plus de 10 000 €</option>
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="wq-message" className={labelClass}>
            Votre projet *
          </label>
          <textarea
            id="wq-message"
            name="message"
            required
            rows={4}
            minLength={20}
            placeholder="Objectifs, public visé, exemples de sites que vous aimez, délais…"
            value={form.message}
            onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
            className={inputClass}
          />
          <p className="mt-1 text-xs text-gray-500">Minimum 20 caractères.</p>
        </div>

        <div className="sr-only" aria-hidden>
          <label htmlFor="wq-website">Ne pas remplir</label>
          <input
            id="wq-website"
            name="website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={form.website}
            onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))}
          />
        </div>

        <label className="flex cursor-pointer items-start gap-2 text-sm text-gray-300">
          <input
            type="checkbox"
            required
            checked={form.consent}
            onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))}
            className="mt-1 rounded border-gray-600 bg-[#36393F] text-[#5865F2] focus:ring-[#5865F2]"
          />
          <span>
            J’accepte d’être recontacté(e) concernant ma demande. *
          </span>
        </label>

        <div className="flex flex-col gap-3 border-t border-gray-700 pt-4 sm:flex-row sm:items-center sm:justify-between">
          <Button type="submit" variant="primary" disabled={loading} className="min-w-[160px] justify-center">
            {loading ? (
              <span className="flex items-center gap-2">
                <Spinner size="sm" />
                Envoi…
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                Envoyer ma demande
              </span>
            )}
          </Button>
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-center text-sm text-[#5865F2] hover:underline sm:text-left"
          >
            Préférez WhatsApp ? Ouvrir la discussion
          </a>
        </div>
      </form>
    </Modal>
  );
};
